// Terminal outcomes of the PHOTO_VERIFICATION step.
//
// PhotoVerification reports exactly one of these per attempt and then stops
// touching the screen. Under `skipToNext` the camera UI is hidden and the
// candidate sees only "Please wait a moment.", so whatever the parent does with
// this outcome is the only thing that can move them forward — an unreported or
// unrecognised outcome strands them on a dead screen.

export const PHOTO_VERIFICATION_SUCCESS_REASON = "photo_uploaded";

// Every failure the component can detect. Kept as data so the reason travels
// into the submitted result instead of being lost to a console line.
export const PHOTO_VERIFICATION_FAILURE_REASONS = Object.freeze([
  "webcam_permission_denied",
  "webcam_access_error",
  "webcam_unavailable",
  "capture_exception",
  "capture_invalid_blob",
  "capture_invalid_type",
  "photo_upload_error",
  "timeout",
]);

// How long the wait screen stays reassuring before it offers the candidate a
// way out. Past this point a healthy step has almost certainly finished, so the
// screen stops saying "please wait" and starts saying "refresh the page".
export const PHOTO_VERIFICATION_SLOW_HINT_MS = 30000;

// Upper bound on the hidden wait. It has to cover a permission prompt the
// candidate still has to click plus a slow upload, while staying short enough
// that a stalled camera does not read as a frozen assessment.
//
// Deliberately after the hint: the hint is the only escape when the page is too
// wedged for this watchdog to fire at all, so the candidate gets the manual
// option first and automatic recovery follows as the backstop.
export const PHOTO_VERIFICATION_TIMEOUT_MS = 45000;

export function isPhotoVerificationSuccess(reason) {
  return reason === PHOTO_VERIFICATION_SUCCESS_REASON;
}

// `error` reaches us as either an i18n key string (the capture/permission
// paths) or an Error (the upload path).
function describeError(error) {
  if (error == null) return "";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message || String(error);
  if (typeof error.message === "string") return error.message;
  return String(error);
}

/**
 * Normalise a `continueToNext` event detail into the record stored on the
 * submitted result. Anything that is not the success reason is recorded as a
 * failure — including a reason this build does not know about — so a verification
 * that did not happen is never filed as one that did.
 */
export function summarizePhotoVerificationOutcome(
  detail = {},
  timestamp = new Date().toISOString(),
) {
  const reason =
    typeof detail.reason === "string" && detail.reason ? detail.reason : "unknown";

  return {
    status: isPhotoVerificationSuccess(reason) ? "SUCCESS" : "FAILED",
    reason,
    message: describeError(detail.error),
    attempt: Number.isFinite(detail.attempt) ? detail.attempt : null,
    at: timestamp,
  };
}
